"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { readSession, removeSession, writeSession } from "@/helpers/safeStorage";

/**
 * La signature des formulaires de /outils, tracée une fois et reprise ensuite.
 *
 * POURQUOI LA SESSION, ET PAS L'APPAREIL. Quelqu'un qui crée son entreprise
 * remplit trois documents d'affilée le même jour : la non-condamnation,
 * l'information au conjoint, l'autorisation de domiciliation. Retracer sa
 * signature à la souris trois fois est pénible, et le résultat est laid une
 * fois sur deux. `sessionStorage` couvre exactement ce besoin et rien de plus :
 * la signature suit la visite, et disparaît à la fermeture de l'onglet.
 *
 * `localStorage` a été écarté volontairement. Il ne servirait qu'à celui qui
 * revient des semaines plus tard, ce qui est rare, et il laisserait une
 * signature manuscrite dans le navigateur d'un poste partagé, ce qui ne l'est
 * pas. Si la persistance longue devient un besoin, elle se fera avec une case
 * décochée par défaut, un bouton pour effacer, et la promesse du premier écran
 * réécrite : aujourd'hui elle dit que tout s'efface avec l'onglet, et c'est
 * vrai.
 *
 * Ce que vaut une signature tracée ici : ce n'est pas une signature
 * électronique au sens de l'article 1367 du code civil, dont la présomption de
 * fiabilité est réservée au procédé qualifié. Ce n'est pas non plus une absence
 * de signature. Le guichet unique n'exige aucune signature électronique sur les
 * pièces jointes à une formalité, seulement sur la formalité elle-même.
 */

const CLE = "tools_signature";

/**
 * Un store externe minuscule autour du stockage de session.
 *
 * Lire le stockage dans un effet puis appeler setState marche, mais provoque le
 * rendu en cascade que React 19 signale : le serveur rend une page sans
 * signature, le client la rend une fois vide, puis une seconde fois remplie.
 * `useSyncExternalStore` dit exactement cela dans le bon ordre, avec un
 * instantané serveur vide et un instantané client lu une seule fois.
 */
const abonnes = new Set();
/** null tant que la session n'a pas été lue, pour ne la lire qu'une fois. */
let cache = null;

function lire() {
    if (cache === null) cache = readSession(CLE, "");
    return cache;
}

/** Vide côté serveur : c'est ce que le HTML rendu contient. */
function lireAuServeur() {
    return "";
}

function souscrire(rappel) {
    abonnes.add(rappel);
    return () => abonnes.delete(rappel);
}

function ecrire(valeur) {
    cache = valeur;
    if (valeur) writeSession(CLE, valeur);
    else removeSession(CLE);
    for (const rappel of abonnes) rappel();
}

/**
 * La signature de la visite.
 *
 * Elle ne sait pas qui l'a tracée ni quand : savoir si le lecteur y a touché
 * sur l'écran courant regarde le pavé, qui est le seul à voir passer les
 * traits, et ce partage évite de lire un ref pendant le rendu.
 */
export function useSignature() {
    const signature = useSyncExternalStore(souscrire, lire, lireAuServeur);
    const poser = useCallback((valeur) => ecrire(valeur), []);
    return { signature, poser };
}

/**
 * Le pavé où l'on signe.
 *
 * Il reste blanc en thème sombre : ce n'est pas une surface d'interface, c'est
 * le papier sur lequel la signature sera imprimée. Un trait blanc sur fond
 * sombre disparaîtrait au téléchargement.
 *
 * Les libellés arrivent en props, un par un, comme partout dans la rubrique :
 * le contrôle de parité FR/EN lit le fichier qui les consomme, et un objet de
 * traduction passé en bloc lui cacherait la moitié des clés.
 */
export function PadSignature({ libelle, aide, effacer, repriseLibelle, valeur, poser }) {
    const toile = useRef(null);
    /* Une signature présente sans qu'on ait tracé ici vient forcément du
       document précédent : c'est ce qu'on dit au lecteur, plutôt que de la
       faire apparaître sans explication. */
    const [traceeIci, setTraceeIci] = useState(false);
    const reprise = Boolean(valeur) && !traceeIci;

    /** Dimensionne la toile en pixels réels, une seule fois. */
    const preparer = useCallback(() => {
        const c = toile.current;
        if (!c) return null;
        if (!c.dataset.pret) {
            const ratio = window.devicePixelRatio || 1;
            const boite = c.getBoundingClientRect();
            c.width = boite.width * ratio;
            c.height = boite.height * ratio;
            const ctx = c.getContext("2d");
            ctx.scale(ratio, ratio);
            ctx.lineWidth = 2;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.strokeStyle = "#05060F";
            c.dataset.pret = "1";
        }
        return c.getContext("2d");
    }, []);

    /* Une signature reprise doit se voir, sinon le formulaire annonce une
       signature que le pavé montre vide. Elle est redessinée à l'échelle et
       calée en haut à gauche : la toile est fluide en largeur, et l'onglet peut
       avoir changé de taille entre deux documents. */
    useEffect(() => {
        const ctx = preparer();
        if (!ctx || !reprise || !valeur) return undefined;

        let annule = false;
        const image = new window.Image();
        image.onload = () => {
            if (annule) return;
            const boite = toile.current.getBoundingClientRect();
            const echelle = Math.min(
                boite.width / image.width,
                boite.height / image.height,
                1,
            );
            ctx.drawImage(image, 0, 0, image.width * echelle, image.height * echelle);
        };
        image.src = valeur;
        return () => {
            annule = true;
        };
    }, [preparer, reprise, valeur]);

    const trace = useRef(false);

    const point = (e) => {
        const boite = toile.current.getBoundingClientRect();
        return { x: e.clientX - boite.left, y: e.clientY - boite.top };
    };

    const commencer = (e) => {
        const ctx = preparer();
        if (!ctx) return;
        trace.current = true;
        toile.current.setPointerCapture(e.pointerId);
        const { x, y } = point(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const continuer = (e) => {
        if (!trace.current) return;
        const ctx = preparer();
        const { x, y } = point(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const finir = () => {
        if (!trace.current) return;
        trace.current = false;
        setTraceeIci(true);
        poser(toile.current.toDataURL("image/png"));
    };

    const vider = () => {
        const c = toile.current;
        if (!c) return;
        c.getContext("2d").clearRect(0, 0, c.width, c.height);
        setTraceeIci(true);
        poser("");
    };

    return (
        <div>
            <div className="flex justify-between items-baseline mb-1.5 gap-3">
                <span className="text-xs text-gray-500 dark:text-lightBlue/70">{libelle}</span>
                {valeur && (
                    <button
                        type="button"
                        onClick={vider}
                        className="text-xs text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200 shrink-0"
                    >
                        {effacer}
                    </button>
                )}
            </div>
            <canvas
                ref={toile}
                onPointerDown={commencer}
                onPointerMove={continuer}
                onPointerUp={finir}
                onPointerLeave={finir}
                className="w-full h-32 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white touch-none cursor-crosshair"
            />
            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1.5 mb-0">
                {reprise ? repriseLibelle : aide}
            </p>
        </div>
    );
}
